import styled, { css } from 'styled-components'
import { media } from '../../../utils/mediaQuery'

export const FilterPanelContainer = styled.div<{
  $collapsed?: boolean
  $width?: number
}>`
  width: ${({ $collapsed, $width }) => ($collapsed ? '0' : $width ? `${$width}px` : '280px')};
  /* Floor matches the resize handle. Narrower than this, filter controls overflow. */
  min-width: ${({ $collapsed }) => ($collapsed ? '0' : '270px')};
  background: ${({ theme }) => theme.colors.background.primary};
  border-right: 1px solid ${({ theme }) => theme.colors.border.default};
  position: relative;
  transition: ${({ $width, theme }) =>
    $width ? 'none' : `width ${theme.transitions.default}, min-width ${theme.transitions.default}`};
  overflow-x: hidden;
  overflow-y: visible;
  display: flex;
  flex-direction: column;

  ${media.tabletDown} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    min-width: 0;
    max-height: calc(100dvh - var(--header-height, 48px));
    height: calc(100dvh - var(--header-height, 48px));
    border-right: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: ${({ theme }) => theme.borderRadius.large}
      ${({ theme }) => theme.borderRadius.large} 0 0;
    box-shadow: ${({ theme }) => theme.shadows.xl};
    z-index: ${({ theme }) => theme.zIndex.modal};
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: env(safe-area-inset-bottom, 0);
    transform: translateY(${({ $collapsed }) => ($collapsed ? '100%' : '0')});
    ${({ $collapsed }) =>
      $collapsed &&
      css`
        pointer-events: none;
      `}
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @media print {
    display: none !important;
    visibility: hidden !important;
    width: 0 !important;
    min-width: 0 !important;
  }
`

/** Drag handle visual for mobile bottom sheet */
export const MobileSheetHandle = styled.div`
  display: none;

  ${media.tabletDown} {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0 6px;
    cursor: grab;
    touch-action: none;
    flex-shrink: 0;

    &:active {
      cursor: grabbing;
    }

    &::after {
      content: '';
      width: 36px;
      height: 4px;
      background: ${({ theme }) => theme.colors.border.default};
      border-radius: ${({ theme }) => theme.borderRadius.round};
    }
  }
`

export const FilterHeader = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
`

export const FilterHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.medium};
`

export const FilterTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin: 0;
`

export const FilterCount = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
  display: inline-block;
`

export const FilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const FilterActionsSeparator = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.border.default};
  margin: 0 2px;
`

export const QuickFilters = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.small};
  min-height: 128px;
  margin: 0 ${({ theme }) => theme.spacing.medium} ${({ theme }) => theme.spacing.medium};
  padding: 10px ${({ theme }) => theme.spacing.xsmall} ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.xsmall};
  background: #f8f8f8;
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
`

export const QuickFiltersTitle = styled.div`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const QuickFiltersActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
  min-height: 20px;
`

export const QuickFilterChip = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-height: 22px;
  padding: 3px 6px;
  background: ${({ theme }) => theme.colors.primaryLight};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  color: #0294df;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;

  &[aria-selected='true'] {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin: 0 0 0 4px;
    padding: 0;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.circle};
    background: ${({ theme }) => theme.colors.primaryLight};
    color: inherit;
    cursor: pointer;
    transition:
      opacity ${({ theme }) => theme.transitions.fast},
      color ${({ theme }) => theme.transitions.fast},
      background ${({ theme }) => theme.transitions.fast};

    &:hover {
      color: ${({ theme }) => theme.colors.text.primary};
      background: ${({ theme }) => theme.colors.background.primary};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.primary};
      outline-offset: 1px;
    }
  }

  /* Pointer desktops: the remove control overlays the label and does not change the pill size. */
  ${media.tablet} {
    @media (hover: hover) and (pointer: fine) {
      > button {
        position: absolute;
        top: 50%;
        right: 2px;
        z-index: 1;
        width: 20px;
        height: 20px;
        margin: 0;
        opacity: 0;
        pointer-events: none;
        transform: translateY(-50%);
        background: ${({ theme }) => theme.colors.primaryLight};
      }

      &:hover > button,
      &:focus-within > button {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }

  /* Tablets and phones: keep the remove control visible and easier to tap. */
  ${media.tabletDown} {
    min-height: 28px;
    padding: 4px 10px;

    > button {
      position: relative;
      width: 22px;
      height: 22px;

      &::before {
        content: '';
        position: absolute;
        inset: -8px;
      }
    }
  }
`

export const FilterContent = styled.div`
  flex: 1;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.text.secondary};
  }
`

export const DesktopOnly = styled.span`
  display: inline-flex;
  ${media.tabletDown} {
    display: none;
  }
`

export const MobileOnly = styled.span`
  display: none;
  ${media.tabletDown} {
    display: inline-flex;
  }
`

export const FilterToggleButton = styled.button<{ $collapsed?: boolean }>`
  position: absolute;
  right: -32px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 64px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-left: none;
  border-radius: ${({ theme }) => `0 ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0`};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  ${media.tabletDown} {
    display: none;
  }
`
